/**
 * OOXML and HTML generation for Word document structures.
 * Handles TOC, footer, cover page, and SBI header/footer insertion.
 * @module office/ooxml
 */

/* global Word, Office */

import { escHtml } from "../utils/html";
import { visibilityDetail } from "../utils/helpers";

/**
 * Detects whether the add-in is running on Word Desktop (vs Word Online).
 * @returns {boolean}
 */
export function isWordDesktop() {
  try {
    return Office.context.requirements.isSetSupported("WordApiDesktop", "1.1");
  } catch (_) {
    return true;
  }
}

/**
 * Generates OOXML for a Table of Contents field with section break.
 * @returns {string} OOXML package string
 */
export function buildTocOoxml() {
  return `<pkg:package xmlns:pkg="http://schemas.microsoft.com/office/2006/xmlPackage">
  <pkg:part pkg:name="/_rels/.rels"
      pkg:contentType="application/vnd.openxmlformats-package.relationships+xml" pkg:padding="512">
    <pkg:xmlData>
      <Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
        <Relationship Id="rId1"
          Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument"
          Target="word/document.xml"/>
      </Relationships>
    </pkg:xmlData>
  </pkg:part>
  <pkg:part pkg:name="/word/document.xml"
      pkg:contentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml">
    <pkg:xmlData>
      <w:document xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">
        <w:body>
          <w:p>
            <w:pPr><w:jc w:val="left"/></w:pPr>
            <w:r>
              <w:rPr><w:b/><w:sz w:val="40"/><w:szCs w:val="40"/></w:rPr>
              <w:t>Sommaire</w:t>
            </w:r>
          </w:p>
          <w:p>
            <w:r><w:fldChar w:fldCharType="begin"/></w:r>
            <w:r><w:instrText xml:space="preserve"> TOC \\o "1-3" \\h \\z \\u </w:instrText></w:r>
            <w:r><w:fldChar w:fldCharType="separate"/></w:r>
            <w:r>
              <w:rPr><w:color w:val="888888"/><w:sz w:val="18"/></w:rPr>
              <w:t>Faites Ctrl+A puis F9 pour afficher le sommaire.</w:t>
            </w:r>
            <w:r><w:fldChar w:fldCharType="end"/></w:r>
          </w:p>
          <w:p>
            <w:r><w:br w:type="page"/></w:r>
          </w:p>
        </w:body>
      </w:document>
    </pkg:xmlData>
  </pkg:part>
</pkg:package>`;
}

/**
 * Inserts a native TOC field on Word Online using the Field API.
 * FieldType.toc is one of only 4 field types supported on Word Online.
 * @param {Word.RequestContext} context
 * @param {Word.Body} body
 */
export async function insertTocFieldOnline(context, body) {
  // Step 1: Insert the TOC field (critical — if this sync throws, nothing was applied)
  const startRange = body.getRange(Word.RangeLocation.start);
  startRange.insertField(Word.InsertLocation.before, Word.FieldType.toc);
  await context.sync();

  // Step 2: Insert "Sommaire" title before the TOC (cosmetic — non-critical)
  try {
    const title = body.insertParagraph("Sommaire", Word.InsertLocation.start);
    title.font.bold = true;
    title.font.size = 20;
    await context.sync();
  } catch (_) { /* title styling failed, TOC is still functional */ }
}

/**
 * Builds a static HTML Table of Contents as fallback for Word Online.
 * Lists headings hierarchically without page numbers.
 * @param {Array<{text: string, level: number}>} headings
 * @returns {string} HTML string
 */
export function buildStaticTocHtml(headings) {
  const indentPt = { 1: 0, 2: 18, 3: 36 };
  const fontSize = { 1: "11pt", 2: "10pt", 3: "9pt" };

  let html = '<p style="font-size:20pt;font-weight:bold">Sommaire</p>';
  for (const h of headings) {
    const indent = indentPt[h.level] || 0;
    const size = fontSize[h.level] || "10pt";
    html += `<p style="margin-left:${indent}pt;font-size:${size};margin:2pt 0;color:#333">${escHtml(h.text)}</p>`;
  }
  html += '<br clear="all" style="page-break-before:always">';
  return html;
}

/**
 * Generates OOXML for a footer with FILENAME and PAGE/NUMPAGES fields.
 * @returns {string} OOXML package string
 */
export function buildFooterOoxml() {
  const ns = 'xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main"';
  const fileRPr = '<w:rPr><w:sz w:val="18"/><w:szCs w:val="18"/><w:color w:val="767171"/></w:rPr>';
  const pageRPr = '<w:rPr><w:sz w:val="18"/><w:szCs w:val="18"/><w:color w:val="000037"/></w:rPr>';

  const field = (name, placeholder, rPr) =>
    `<w:r>${rPr}<w:fldChar w:fldCharType="begin"/></w:r>` +
    `<w:r>${rPr}<w:instrText xml:space="preserve"> ${name} </w:instrText></w:r>` +
    `<w:r>${rPr}<w:fldChar w:fldCharType="separate"/></w:r>` +
    `<w:r>${rPr}<w:t>${escHtml(placeholder)}</w:t></w:r>` +
    `<w:r>${rPr}<w:fldChar w:fldCharType="end"/></w:r>`;

  return `<pkg:package xmlns:pkg="http://schemas.microsoft.com/office/2006/xmlPackage">
  <pkg:part pkg:name="/_rels/.rels"
      pkg:contentType="application/vnd.openxmlformats-package.relationships+xml">
    <pkg:xmlData>
      <Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
        <Relationship Id="rId1"
          Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument"
          Target="word/document.xml"/>
      </Relationships>
    </pkg:xmlData>
  </pkg:part>
  <pkg:part pkg:name="/word/document.xml"
      pkg:contentType="application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml">
    <pkg:xmlData>
      <w:document ${ns}>
        <w:body>
          <w:p>
            <w:pPr>
              <w:pBdr>
                <w:bottom w:val="single" w:sz="6" w:space="4" w:color="000040"/>
              </w:pBdr>
              <w:spacing w:before="0" w:after="0"/>
            </w:pPr>
          </w:p>
          <w:tbl>
            <w:tblPr>
              <w:tblW w:w="5000" w:type="pct"/>
              <w:tblBorders>
                <w:top w:val="none" w:sz="0" w:space="0" w:color="auto"/>
                <w:left w:val="none" w:sz="0" w:space="0" w:color="auto"/>
                <w:bottom w:val="none" w:sz="0" w:space="0" w:color="auto"/>
                <w:right w:val="none" w:sz="0" w:space="0" w:color="auto"/>
                <w:insideH w:val="none" w:sz="0" w:space="0" w:color="auto"/>
                <w:insideV w:val="none" w:sz="0" w:space="0" w:color="auto"/>
              </w:tblBorders>
              <w:tblCellMar>
                <w:top w:w="80" w:type="dxa"/>
                <w:bottom w:w="0" w:type="dxa"/>
              </w:tblCellMar>
            </w:tblPr>
            <w:tblGrid>
              <w:gridCol w:w="4500"/>
              <w:gridCol w:w="4500"/>
            </w:tblGrid>
            <w:tr>
              <w:tc>
                <w:p>
                  <w:pPr><w:spacing w:before="20" w:after="0"/><w:rPr><w:sz w:val="18"/><w:szCs w:val="18"/></w:rPr></w:pPr>
                  ${field("FILENAME", "Document", fileRPr)}
                </w:p>
              </w:tc>
              <w:tc>
                <w:p>
                  <w:pPr><w:jc w:val="right"/><w:spacing w:before="20" w:after="0"/><w:rPr><w:sz w:val="18"/><w:szCs w:val="18"/></w:rPr></w:pPr>
                  <w:r>${pageRPr}<w:t xml:space="preserve">Page : </w:t></w:r>
                  ${field("PAGE", "1", pageRPr)}
                  <w:r>${pageRPr}<w:t>/</w:t></w:r>
                  ${field("NUMPAGES", "1", pageRPr)}
                </w:p>
              </w:tc>
            </w:tr>
          </w:tbl>
        </w:body>
      </w:document>
    </pkg:xmlData>
  </pkg:part>
</pkg:package>`;
}

/**
 * Builds footer content using HTML for Word Online.
 * Uses static text from SBI options (dynamic fields like PAGE/FILENAME are not supported on Word Online).
 * @param {Word.RequestContext} context
 * @param {Word.HeaderFooter} footer
 * @param {object} opts - { docType, docLabel, version }
 */
async function insertFooterFieldsOnline(context, footer, opts) {
  const leftText = opts.docLabel || opts.docType || "";
  const rightText = opts.version ? opts.version + " Version" : "";

  const html = `<table style="width:100%;border-collapse:collapse">
    <tr>
      <td style="border:none;border-top:1px solid #000040;font-size:9pt;color:#767171;padding-top:4px">${escHtml(leftText)}</td>
      <td style="border:none;border-top:1px solid #000040;font-size:9pt;color:#000037;text-align:right;padding-top:4px">${escHtml(rightText)}</td>
    </tr>
  </table>`;

  footer.insertHtml(html, Word.InsertLocation.start);
  await context.sync();
}

/**
 * Generates HTML for the SBI cover page.
 * @param {object} options
 * @param {string} options.docType
 * @param {string} options.docLabel
 * @param {string} options.version
 * @param {string} options.visibility
 * @param {string} options.email - User email for author name extraction
 * @param {string} logoB64 - Small logo base64
 * @param {string} bigLogoB64 - Big logo base64
 * @returns {string} HTML string
 */
export function buildCoverPageHtml(options, logoB64, bigLogoB64) {
  const { docType, docLabel, version, visibility, email } = options;
  const author = email.split("@")[0].replace(/\./g, " ").replace(/\b\w/g, c => c.toUpperCase());
  const dateStr = new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  const sp = '<p style="text-align:center">&nbsp;</p>';

  let html = "";
  html += sp + sp;
  html += `<p style="text-align:center;margin:8px 0"><img src="data:image/png;base64,${bigLogoB64}" alt="SBI"></p>`;
  html += sp;
  html += '<hr color="#000040" size="2" width="50%" noshade>';
  html += sp;
  if (docType) html += `<p style="text-align:center;font-size:22pt;font-weight:bold;color:#000037;margin:4px 0">${escHtml(docType)}</p>`;
  html += sp;
  if (docLabel) html += `<p style="text-align:center;font-size:18pt;color:#3B3838;margin:4px 0">${escHtml(docLabel)}</p>`;
  html += sp + sp;
  if (version) html += `<p style="text-align:center;font-size:10pt;font-weight:bold;color:#808080;margin:2px 0">${escHtml(version)} Version</p>`;
  html += `<p style="text-align:center;font-size:9pt;color:#79759F;margin:2px 0">${escHtml(visibility)} \u2013 ${escHtml(visibilityDetail(visibility))}</p>`;
  html += sp + sp;
  html += `<p style="text-align:center;font-size:10pt;color:#555;margin:2px 0">${escHtml(dateStr)}</p>`;
  html += `<p style="text-align:center;font-size:10pt;color:#555;margin:2px 0">${escHtml(author)}</p>`;
  return html;
}

/**
 * Inserts SBI-branded header and footer into the Word document.
 * @param {Word.RequestContext} context - Word request context
 * @param {object} options - SBI options (docType, docLabel, version, visibility, bannerText)
 * @param {string} logoB64 - Logo image base64
 * @param {boolean} hasCoverPage - Whether a cover page section exists
 * @returns {Promise<void>}
 */
export async function insertSbiHeaderFooter(context, options, logoB64, hasCoverPage) {
  const { docType, docLabel, version, visibility, bannerText } = options;
  const desktop = isWordDesktop();

  const allSections = context.document.sections;
  allSections.load("items");
  await context.sync();

  const startIdx = hasCoverPage ? 1 : 0;
  if (startIdx >= allSections.items.length) return;

  // ─── En-tête : logo à gauche, infos à droite ───
  const headerHtml = `<table style="width:100%;border-collapse:collapse">
    <tr>
      <td style="border:none;width:30%;vertical-align:middle;padding:0">
        <img src="data:image/png;base64,${logoB64}" style="height:45px" alt="SBI">
      </td>
      <td style="border:none;text-align:right;vertical-align:middle;font-family:Segoe UI,sans-serif;line-height:1.6">
        ${docType ? `<span style="font-size:14pt;font-weight:bold;color:#000037">${escHtml(docType)}</span><br>` : ""}${docLabel ? `<span style="font-size:14pt;color:#3B3838">${escHtml(docLabel)}</span><br>` : ""}${version ? `<span style="font-size:10pt;font-weight:bold;color:#808080">${escHtml(version)} Version</span><br>` : ""}<span style="font-size:9pt;color:#79759F">${escHtml(visibility)} \u2013 ${escHtml(visibilityDetail(visibility))}</span>
      </td>
    </tr>
  </table>`;

  const footerOoxml = buildFooterOoxml();

  // ─── Réduire les marges (try/catch : WordApiDesktop 1.3 requis) ───
  try {
    for (const sec of allSections.items) {
      sec.pageSetup.footerDistance = 0;
      sec.pageSetup.bottomMargin = 40;
    }
    await context.sync();
  } catch (_) { /* API non disponible sur certaines versions Mac */ }

  // ─── Appliquer en-tête et pied de page à TOUTES les sections de contenu ───
  for (let i = startIdx; i < allSections.items.length; i++) {
    const section = allSections.items[i];
    const header = section.getHeader(Word.HeaderFooterType.primary);
    const footer = section.getFooter(Word.HeaderFooterType.primary);

    header.clear();
    header.insertHtml(headerHtml, Word.InsertLocation.start);
    footer.clear();

    if (desktop) {
      footer.insertOoxml(footerOoxml, Word.InsertLocation.start);
      await context.sync();
    } else {
      await context.sync();
      try {
        await insertFooterFieldsOnline(context, footer, { docType, docLabel, version });
      } catch (e) {
        console.warn("Footer online insertion failed:", e.message);
      }
    }

    // ─── Bandeau SBI : tableau 1 cellule, fond bleu, texte blanc ───
    const bannerTable = footer.insertTable(1, 1, Word.InsertLocation.end, [[bannerText]]);
    bannerTable.font.color = "#FFFFFF";
    bannerTable.font.size = 10;
    bannerTable.alignment = Word.Alignment.centered;

    bannerTable.getBorder(Word.BorderLocation.top).type = Word.BorderType.none;
    bannerTable.getBorder(Word.BorderLocation.bottom).type = Word.BorderType.none;
    bannerTable.getBorder(Word.BorderLocation.left).type = Word.BorderType.none;
    bannerTable.getBorder(Word.BorderLocation.right).type = Word.BorderType.none;
    bannerTable.getBorder(Word.BorderLocation.insideHorizontal).type = Word.BorderType.none;
    bannerTable.getBorder(Word.BorderLocation.insideVertical).type = Word.BorderType.none;

    const bannerCell = bannerTable.getCell(0, 0);
    bannerCell.shadingColor = "#000040";
    bannerCell.body.paragraphs.getFirst().alignment = Word.Alignment.centered;

    bannerTable.width = 680;
    bannerTable.tableIndent = -85;

    const bannerRow = bannerTable.rows.getFirst();
    bannerRow.preferredHeight = 40;
    bannerRow.horizontalAlignment = Word.Alignment.centered;
    bannerRow.verticalAlignment = Word.VerticalAlignment.center;
    await context.sync();

    // Supprimer les espacements résiduels dans le footer
    const footerParas = footer.paragraphs;
    footerParas.load("items");
    await context.sync();
    for (const p of footerParas.items) {
      p.spaceAfter = 0;
      p.spaceBefore = 0;
    }
    const lastPara = footerParas.items[footerParas.items.length - 1];
    if (lastPara) {
      lastPara.font.size = 1;
      lastPara.lineSpacing = 1;
    }
    await context.sync();
  }
}
